import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { io } from 'socket.io-client'
import { authTokenCache } from '../auth-token-cache'
import { SocketContext, type AppSocket, type SocketContextValue } from './socket-context'

interface SocketProviderProps {
  url?: string
  children: ReactNode
}

export function SocketProvider({ url, children }: SocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const socketRef = useRef<AppSocket | null>(null)

  useEffect(() => {
    // Get events service URL from environment or prop
    const eventsUrl = url || window.env?.EVENTS_URL

    if (!eventsUrl) {
      console.warn('EVENTS_URL not configured, Socket.io disabled')
      return
    }

    const initSocket = async () => {
      try {
        const token = await authTokenCache.getToken()

        if (!token) {
          setConnectionError('No auth token available')
          return
        }

        // Get socket.io path (e.g., /events for path-based routing)
        const socketPath = window.env?.EVENTS_PATH || '/events'

        const socket = io(eventsUrl, {
          path: socketPath,
          auth: { token },
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000
        }) as AppSocket

        socket.on('connect', () => {
          console.log('Socket connected:', socket.id)
          setIsConnected(true)
          setConnectionError(null)
        })

        socket.on('disconnect', (reason: string) => {
          console.log('Socket disconnected:', reason)
          setIsConnected(false)
        })

        socket.on('connect_error', (error: Error) => {
          console.error('Socket connection error:', error.message)
          setConnectionError(error.message)
          setIsConnected(false)
        })

        socketRef.current = socket
      } catch (error) {
        console.error('Failed to initialize socket:', error)
        setConnectionError(error instanceof Error ? error.message : 'Unknown error')
      }
    }

    initSocket()

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [url])

  const emit = useCallback<SocketContextValue['emit']>((event, payload) => {
    if (socketRef.current?.connected) {
      // Cast to avoid complex socket.io generic inference issues
      ;(socketRef.current as unknown as { emit: (ev: string, data: unknown) => void }).emit(
        event,
        payload
      )
    } else {
      console.warn('Socket not connected, cannot emit event:', event)
    }
  }, [])

  const value = useMemo<SocketContextValue>(
    () => ({
      socket: socketRef.current,
      isConnected,
      connectionError,
      emit
    }),
    [isConnected, connectionError, emit]
  )

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}
